/**
 * Server-state hooks, one per documented list endpoint.
 *
 * Every hook requires an explicit tenant id and a truthy `enabled` flag. That makes
 * it impossible for a page to fetch business data for the wrong tenant, or to fetch
 * before a tenant is selected, without the mistake showing up as a disabled query.
 *
 * There is no placeholder tenant id: when no tenant is selected the query is simply
 * disabled and no cache entry is created. An earlier revision keyed those calls as
 * `['tenant', 'none', ...]`, which could collide across tenants and let business
 * rows be cached outside any tenant scope.
 */

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  fetchAlerts,
  fetchContents,
  fetchJobs,
  fetchReports,
  fetchTopics,
  type AlertQuery,
  type ListQuery,
  type ContentQuery,
} from '../api/client';
import { DEFAULT_PAGE_SIZE, queryKeys } from '../app/queryKeys';
import type { AlertItem, ContentItem, JobItem, ReportItem, Topic } from '../api/types';

export interface TenantListHookOptions {
  readonly tenantId: string | null;
  readonly enabled?: boolean;
  readonly limit?: number;
  /** Keep the previous page on screen while a filter change refetches. */
  readonly keepPrevious?: boolean;
}

function listQuery(limit: number): ListQuery {
  return { limit };
}

/**
 * Selects `{ items }` into a stable reference.
 *
 * `query.data?.items ?? []` allocates a new array on every render while the query
 * is loading, so any `useMemo`/`useEffect` that depends on it re-runs each time and
 * the dependency array is effectively meaningless. Returning a module-level empty
 * array keeps the reference stable, which is what makes those dependencies work.
 */
const NO_ITEMS: readonly never[] = [];

export function useStableItems<T>(items: readonly T[] | undefined): readonly T[] {
  return useMemo(() => items ?? NO_ITEMS, [items]);
}

export function useTopicsQuery(options: TenantListHookOptions): UseQueryResult<{ items: Topic[] }> {
  const limit = options.limit ?? DEFAULT_PAGE_SIZE;
  const tenantId = options.tenantId;
  return useQuery({
    queryKey: queryKeys.topics(tenantId ?? '', limit),
    queryFn: ({ signal }) => fetchTopics(listQuery(limit), signal),
    enabled: tenantId !== null && (options.enabled ?? true),
  });
}

export function useContentsQuery(
  options: TenantListHookOptions & ContentQuery,
): UseQueryResult<{ items: ContentItem[]; next_cursor: string | null }> {
  const limit = options.limit ?? DEFAULT_PAGE_SIZE;
  const tenantId = options.tenantId;
  return useQuery({
    queryKey: [
      ...queryKeys.contents(tenantId ?? '', limit),
      options.keyword ?? '',
      options.scope ?? 'all',
      options.source ?? '',
      options.start ?? '',
      options.end ?? '',
      options.cursor ?? '',
    ],
    ...(options.keepPrevious === true ? { placeholderData: keepPreviousData } : {}),
    queryFn: ({ signal }) =>
      fetchContents(
        {
          limit,
          ...(options.keyword !== undefined ? { keyword: options.keyword } : {}),
          ...(options.scope !== undefined ? { scope: options.scope } : {}),
          ...(options.source !== undefined ? { source: options.source } : {}),
          ...(options.start !== undefined ? { start: options.start } : {}),
          ...(options.end !== undefined ? { end: options.end } : {}),
          ...(options.cursor !== undefined ? { cursor: options.cursor } : {}),
        },
        signal,
      ),
    enabled: tenantId !== null && (options.enabled ?? true),
  });
}

export function useAlertsQuery(
  options: TenantListHookOptions & Partial<AlertQuery> & { readonly refetchIntervalMs?: number },
): UseQueryResult<{ items: AlertItem[] }> {
  const limit = options.limit ?? DEFAULT_PAGE_SIZE;
  const tenantId = options.tenantId;
  return useQuery({
    queryKey: [
      ...queryKeys.alerts(tenantId ?? '', limit),
      options.severity ?? '',
      options.status ?? '',
      options.unread_only ?? false,
    ],
    queryFn: ({ signal }) =>
      fetchAlerts(
        {
          limit,
          ...(options.severity !== undefined ? { severity: options.severity } : {}),
          ...(options.status !== undefined ? { status: options.status } : {}),
          ...(options.unread_only !== undefined ? { unread_only: options.unread_only } : {}),
        },
        signal,
      ),
    enabled: tenantId !== null && (options.enabled ?? true),
    ...(options.keepPrevious === true ? { placeholderData: keepPreviousData } : {}),
    ...(options.refetchIntervalMs !== undefined
      ? { refetchInterval: options.refetchIntervalMs }
      : {}),
  });
}

export function useJobsQuery(options: TenantListHookOptions): UseQueryResult<{ items: JobItem[] }> {
  const limit = options.limit ?? DEFAULT_PAGE_SIZE;
  const tenantId = options.tenantId;
  return useQuery({
    queryKey: queryKeys.jobs(tenantId ?? '', limit),
    queryFn: ({ signal }) => fetchJobs(listQuery(limit), signal),
    enabled: tenantId !== null && (options.enabled ?? true),
  });
}

export function useReportsQuery(
  options: TenantListHookOptions,
): UseQueryResult<{ items: ReportItem[] }> {
  const limit = options.limit ?? DEFAULT_PAGE_SIZE;
  const tenantId = options.tenantId;
  return useQuery({
    queryKey: queryKeys.reports(tenantId ?? '', limit),
    queryFn: ({ signal }) => fetchReports(listQuery(limit), signal),
    enabled: tenantId !== null && (options.enabled ?? true),
  });
}
